// SVG utility functions for the application

/**
 * Centralized SVG assets for the application
 * All SVG content is now stored in svgConstants.ts for better organization
 */

import { SVG_CONSTANTS } from "./svgConstants";

type SvgNoArgFn = () => string;
type SvgFn = SvgNoArgFn | ((color?: string) => string);

const svgMap = {
  toggleSvg: () => SVG_CONSTANTS.toggleSvg,
  rightChevronSvg: SVG_CONSTANTS.rightChevronSvg,
  circleAlertSvg: SVG_CONSTANTS.circleAlertSvg,
  circleAlertRedSvg: () => SVG_CONSTANTS.circleAlertRedSvg,
  circleWarningOrangeSvg: () => SVG_CONSTANTS.circleWarningOrangeSvg,
  circleCheckSvg: SVG_CONSTANTS.circleCheckSvg,
  refreshLinesSvg: SVG_CONSTANTS.refreshLinesSvg,
  loanLightLogoDarkSvg: () => SVG_CONSTANTS.loanLightLogoDarkSvg,
  loanLightLogoLightSvg: () => SVG_CONSTANTS.loanLightLogoLightSvg,
  loadingSpinnerSvg: SVG_CONSTANTS.loadingSpinnerSvg,
  loanLightIconSvg: SVG_CONSTANTS.loanLightIconSvg,
  xSvg: SVG_CONSTANTS.xSvg,
  spinningDotsSvg: SVG_CONSTANTS.spinningDotsSvg,
} as const satisfies Record<string, SvgFn>;

export type SvgKey = keyof typeof svgMap;

export const getAllSvgKeys = (): SvgKey[] => Object.keys(svgMap) as SvgKey[];

/**
 * Retrieves SVG content by key with type safety and optional stroke color customization
 *
 * Available SVG keys:
 * - toggleSvg
 * - rightChevronSvg
 * - circleAlertSvg
 * - circleAlertRedSvg
 * - circleWarningOrangeSvg
 * - circleCheckSvg
 * - refreshLinesSvg
 * - loanLightLogoDarkSvg
 * - loanLightLogoLightSvg
 * - loadingSpinnerSvg
 * - loanLightIconSvg
 * - xSvg
 * - spinningDotsSvg
 * @param key - The SVG key to retrieve from the available SVG collection
 * @param color - Optional color parameter for SVGs that support color customization
 * @returns SVG content as a string
 */
export const getSvg = (key: SvgKey, color?: string): string => {
  const svgFunction = svgMap[key];
  if (!svgFunction) {
    throw new Error(`Unknown SVG key: ${String(key)}`);
  }

  return color
    ? (svgFunction as (c?: string) => string)(color)
    : (svgFunction as () => string)();
};
